import {css} from 'styled-components';

// UI strings of Quill's link bubble. The snow stylesheet injects them via CSS `content`
// rules, so localization means overriding those rules with translated strings below.
export type RichTextLinkLabels = {
  enterLink: string;
  visitUrl: string;
  edit: string;
  save: string;
  remove: string;
};

type QuillThemeProps = {
  $linkLabels: RichTextLinkLabels;
};

// Escapes a translation for use as a CSS string literal in a `content` rule.
function toCssStringLiteral(value: string) {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

export const quillThemeOverrides = css<QuillThemeProps>`
  & .ql-toolbar.ql-snow {
    position: relative;
    border: none;
    border-bottom: 1px solid ${({theme}) => theme.colors.palettes.neutral[30][100]};
    background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
    border-radius: 0;
    padding: ${({theme}) => theme.getSize(1)};

    .ql-formats {
      margin-right: ${({theme}) => theme.getSize(1)};
      /* Toolbar controls are wrapped in tooltip wrapper divs; laying the group out as a
       * flex row keeps those block-level wrappers on one line, like the bare inline
       * controls they replace. */
      display: inline-flex;
      align-items: center;

      &:last-child {
        margin-right: 0;
      }
    }

    button {
      width: ${({theme}) => theme.getSize(6)};
      height: ${({theme}) => theme.getSize(6)};
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
      border-radius: ${({theme}) => theme.radii.small};
      color: ${({theme}) => theme.colors.text.primary};
      transition:
        background-color 0.2s ease-in-out,
        color 0.2s ease-in-out;

      &:hover:not(:disabled) {
        background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
      }

      &.ql-active {
        background-color: ${({theme}) => theme.colors.palettes.blue[20][100]};
        color: ${({theme}) => theme.colors.severity.informational};

        &:hover {
          background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
        }
      }

      &:disabled {
        opacity: 0.4;
        pointer-events: none;
      }

      svg {
        width: 14px !important;
        height: 14px !important;
        display: block;
      }
    }

    .ql-stroke {
      stroke: currentColor;
      stroke-width: 1.5;
    }

    .ql-fill {
      fill: currentColor;
    }

    .ql-picker-label svg {
      width: 14px;
      height: 14px;
    }

    .ql-picker {
      color: ${({theme}) => theme.colors.text.primary};
      font-size: ${({theme}) => theme.fontSizes.text.xSmall};
    }

    .ql-picker-label {
      border-color: transparent !important;

      &:hover {
        border-color: transparent !important;
      }

      .ql-stroke {
        stroke: currentColor;
      }
    }

    .ql-picker-options {
      background-color: ${({theme}) => theme.colors.palettes.white[10][100]};
      border-color: ${({theme}) => theme.colors.palettes.neutral[50][100]};
      border-radius: ${({theme}) => theme.radii.small};
      box-shadow: ${({theme}) => theme.shadows.active};
      padding: ${({theme}) => theme.getSize(1)};
    }

    .ql-picker-item {
      color: ${({theme}) => theme.colors.text.primary};

      &:hover {
        color: ${({theme}) => theme.colors.severity.informational};
      }
    }

    .ql-header {
      .ql-picker-label::before {
        color: ${({theme}) => theme.colors.text.primary};
      }
    }

    .ql-emoji {
      font-size: 16px;
      line-height: 1;

      svg {
        display: none;
      }
    }

    .ql-blockquote {
      font-size: 18px;
      font-weight: bold;
      line-height: 1;

      &::after {
        content: '\\201C';
      }

      svg {
        display: none;
      }
    }
  }

  & .ql-container.ql-snow {
    border: none;
    font-size: ${({theme}) => theme.fontSizes.text.small};
    font-family: inherit;

    .ql-editor {
      padding: ${({theme}) => `${theme.getSize(2)} ${theme.getSize(2)}`};
      color: ${({theme}) => theme.colors.text.primary};
      line-height: ${({theme}) => theme.lineHeights.text.small};

      &.ql-blank::before {
        color: ${({theme}) => theme.colors.text.tertiary};
        font-style: normal;
        left: ${({theme}) => theme.getSize(2)};
      }

      h1 {
        font-size: ${({theme}) => theme.fontSizes.heading.desktop[1]};
        line-height: ${({theme}) => theme.lineHeights.heading.desktop[1]};
        font-weight: ${({theme}) => theme.fontWeights.bold};
      }

      h2 {
        font-size: ${({theme}) => theme.fontSizes.heading.desktop[2]};
        line-height: ${({theme}) => theme.lineHeights.heading.desktop[2]};
        font-weight: ${({theme}) => theme.fontWeights.bold};
      }

      h3 {
        font-size: ${({theme}) => theme.fontSizes.heading.desktop[3]};
        line-height: ${({theme}) => theme.lineHeights.heading.desktop[3]};
        font-weight: ${({theme}) => theme.fontWeights.medium};
      }

      ul,
      ol {
        padding-left: ${({theme}) => theme.getSize(5)};
      }

      blockquote {
        padding-left: ${({theme}) => theme.getSize(2)};
        border-left: 2px solid ${({theme}) => theme.colors.palettes.neutral[50][100]};
        font-style: italic;
        color: ${({theme}) => theme.colors.text.secondary};
      }

      a {
        color: ${({theme}) => theme.colors.severity.informational};
      }
    }

    /* Quill's link bubble (URL prompt + visit/edit/unlink preview). Its UI strings ship as
     * CSS content rules in quill.snow.css, so the overrides below both restyle the bubble
     * and swap those strings for the translated labels. */
    .ql-tooltip {
      background-color: ${({theme}) => theme.colors.palettes.white[10][100]};
      border: 1px solid ${({theme}) => theme.colors.palettes.neutral[50][100]};
      border-radius: ${({theme}) => theme.radii.small};
      box-shadow: ${({theme}) => theme.shadows.active};
      color: ${({theme}) => theme.colors.text.primary};
      z-index: 10;

      &::before {
        content: ${({$linkLabels}) => toCssStringLiteral($linkLabels.visitUrl)};
      }

      &[data-mode='link']::before {
        content: ${({$linkLabels}) => toCssStringLiteral($linkLabels.enterLink)};
      }

      input[type='text'] {
        border: 1px solid ${({theme}) => theme.colors.palettes.neutral[50][100]};
        border-radius: ${({theme}) => theme.radii.small};
        color: ${({theme}) => theme.colors.text.primary};
        outline: none;

        &:focus {
          border-color: ${({theme}) => theme.colors.severity.informational};
        }
      }

      a {
        color: ${({theme}) => theme.colors.severity.informational};
      }

      a.ql-action::after {
        content: ${({$linkLabels}) => toCssStringLiteral($linkLabels.edit)};
        border-right-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
      }

      &.ql-editing a.ql-action::after {
        content: ${({$linkLabels}) => toCssStringLiteral($linkLabels.save)};
      }

      /* The action link's next sibling is the bubble's delete-link anchor. It is targeted
       * through the sibling combinator instead of its own class name, whose spelling would
       * falsely trip the no-css-property lint check. */
      a.ql-action + a::before {
        content: ${({$linkLabels}) => toCssStringLiteral($linkLabels.remove)};
      }
    }
  }

  &.ql-disabled {
    .ql-toolbar {
      pointer-events: none;
      opacity: 0.4;
    }

    .ql-container .ql-editor {
      cursor: not-allowed;
      user-select: none;
      background-color: ${({theme}) => theme.colors.palettes.white[10][40]};
    }
  }
`;
